"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const trustBadges = [
  { id: 'badge_messages', icon: 'PaperAirplaneIcon', text: '10,000+ Messages Delivered' },
  { id: 'badge_security', icon: 'ShieldCheckIcon', text: 'Bank-Level Security' },
  { id: 'badge_families', icon: 'HeartIcon', text: 'Loved by 5,000+ Families' }];


  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-stone-900 to-stone-800">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="/images/hero-old.jpg"
          alt="Vintage handwritten letter on wooden desk with soft warm lighting and family photos in background"
          className="w-full h-full object-cover opacity-40" />
        
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
        {/* Trust Badges */}
        <div
          className={`flex flex-wrap justify-center gap-4 mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }
          style={{ transitionDelay: '100ms' }}>
          
          {trustBadges.map((badge) =>
          <div
            key={badge.id}
            className="glass-panel-dark px-5 py-3 rounded-full flex items-center gap-3 text-white text-sm font-medium hover:scale-105 transition-transform">
            
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Icon name={badge.icon as any} size={18} className="text-white" />
              </div>
              {badge.text}
            </div>
          )}
        </div>

        {/* Main Headline */}
        <h1
          className={`font-jakarta font-bold text-5xl md:text-7xl lg:text-8xl text-white mb-8 leading-tight tracking-tight transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }
          style={{ transitionDelay: '200ms' }}>
          
          Your Final Words,
          <br />
          <span className="gradient-text">Preserved Forever</span>
        </h1>

        {/* Subheadline */}
        <p
          className={`text-white/80 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }
          style={{ transitionDelay: '300ms' }}>
          
          Create meaningful messages, videos, and memories that will be delivered exactly when you
          choose—on special dates, milestones, or after you&apos;re gone.
        </p>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }
          style={{ transitionDelay: '500ms' }}>
          
          <Link
            href="#start"
            className="px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold text-base hover:bg-primary/90 transition-all shadow-2xl hover:shadow-primary/50 hover:scale-105 flex items-center gap-3 group">
            
            Start Your Legacy
            <Icon
              name="ArrowRightIcon"
              size={20}
              className="group-hover:translate-x-1 transition-transform" />
            
          </Link>
          <Link
            href="#how-it-works"
            className="px-8 py-4 glass-panel-dark text-white rounded-full font-medium text-base hover:bg-white/20 transition-all flex items-center gap-3 group">
            
            <Icon name="PlayCircleIcon" size={24} />
            Watch How It Works
          </Link>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`
        }
        style={{ transitionDelay: '700ms' }}>
        
        <div className="flex flex-col items-center gap-2 text-white/60">
          <span className="text-xs uppercase tracking-wider">Scroll to explore</span>
          <Icon name="ChevronDownIcon" size={24} className="animate-bounce" />
        </div>
      </div>
    </section>);

};

export default HeroSection;